import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ProjectsActions } from 'redux/actions';
import { Container, FormWrapper, ModalV2 } from 'utils';
import styled from '@emotion/styled';
import { Form } from 'antd';
import FormItem from 'antd/es/form/FormItem';
import {
  IconKapital,
  IconPersonen,
  IconPrivatperson,
  IconVerein,
} from 'images';
import { LoginFormV2, RegisteredForm } from '../../../components';
import isEmpty from 'loadsh/isEmpty';

const HeaderText = styled.h1`
  font-family: 'Inter';
  font-style: normal;
  font-weight: 600;
  font-size: 30px;
  line-height: 38px;
  color: #181d27;
`;

const ClickableFormItem = styled(FormItem)`
  cursor: pointer;
  background-color: ${({ $isSelected }) =>
    $isSelected ? '#f5f5f5' : 'transparent'};
  transition: background-color 0.3s ease;
  flex-direction: row !important; /* Force horizontal layout */
  padding-top: 10px;
  padding-bottom: 10px;
  border-radius: 10px;
  align-content: center;
  align-items: center;

  &:hover {
    border-radius: 10px;
    background-color: #f0f0f0;
  }

  .ant-form-item-control-input {
    display: flex;
    flex-direction: row !important;
    align-items: center;
    width: 100%;
  }

  .ant-form-item-control-input-content {
    display: flex;
    flex-direction: row !important;
    align-items: center;
    width: 100%;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  margin-right: 12px;
  flex-shrink: 0;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.span`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 0 0 4px 0;
`;

const Description = styled.span`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 0;
  padding: 0;

  color: #535862;
`;

const getStoredJson = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : {};
  } catch {
    return {};
  }
};

const isUserLoggedIn = () => {
  const client = localStorage.getItem('client');
  const uid = localStorage.getItem('uid');
  const access = localStorage.getItem('access-token');
  const user = localStorage.getItem('user');

  return Boolean(user && !isEmpty(user) && access && uid && client);
};

export class StripeAccount extends Component {
  static propTypes = {
    updateDraft: PropTypes.func.isRequired,
    project: PropTypes.object.isRequired,
    projectId: PropTypes.string,
    setCanMove: PropTypes.func.isRequired,
    currentStep: PropTypes.number.isRequired,
  };

  state = {
    showLoginModal: false,
    showRegisteredModal: false,
  };

  handelCloseLoginModal = () => {
    this.setState({ showLoginModal: false });
  };
  handelCloseRegisteredModal = () => {
    this.setState({ showRegisteredModal: false });
  };

  onFinish = (value) => {
    const parsedValue = { business_type: value.id };
    const { setCanMove, projectId, updateDraft, currentStep } = this.props;
    setCanMove(true);

    if (projectId) {
      updateDraft(projectId, parsedValue);
      sessionStorage.removeItem('userType');
    } else {
      sessionStorage.setItem('userType', JSON.stringify(parsedValue));
    }

    this.handleNextStepAfterSelection(parsedValue);
  };

  handleNextStepAfterSelection = (selectedUserType) => {
    if (!this.isEventProject()) {
      this.props.handleSteps(3);
      return;
    }

    if (isUserLoggedIn()) {
      this.props.handleSteps(4);
      return;
    }

    this.setState({
      showLoginModal: true,
      onLoginSuccess: () => {
        if (!this.props.projectId) {
          sessionStorage.setItem('userType', JSON.stringify(selectedUserType));
        }
        this.props.handleSteps(4);
      },
      onRegisterSuccess: () => {
        this.setState({
          showLoginModal: false,
          showRegisteredModal: true,
        });
      },
    });
  };

  onReturn = () => {
    this.props.handleSteps(1);
  };

  getCategoryData = () => {
    const { project, projectId } = this.props;
    const storedCategory = getStoredJson('categoryType');

    // A new-project flow must not inherit the previously viewed/edited
    // project that may still be present in Redux.
    if (!projectId) {
      return storedCategory;
    }

    return {
      ...storedCategory,
      ...(project?.categoryType || {}),
      project_type:
        project?.project_type ||
        project?.categoryType?.project_type ||
        storedCategory.project_type,
      country:
        project?.categoryType?.country ||
        project?.initiator_country ||
        project?.country ||
        storedCategory.country,
    };
  };

  isEventProject = () => {
    const { project, projectId } = this.props;
    const categoryData = this.getCategoryData();

    if (!projectId) {
      return categoryData.project_type === 'event';
    }

    return Boolean(
      categoryData.project_type === 'event' ||
      project?.fundingTarget?.is_ticketable ||
        project?.is_ticketable
    );
  };

  getSelectedBusinessType = () => {
    const { project, projectId } = this.props;
    const storedUserType = getStoredJson('userType');

    return projectId
      ? project?.business_type || storedUserType.business_type
      : storedUserType.business_type;
  };

  render() {
    const selectedCountry = this.getCategoryData().country;
    const selectedBusinessType = this.getSelectedBusinessType();
    const isGermany = selectedCountry === 'DE';

    const entityTypes = [
      {
        id: 'individual',
        title: 'Privatperson',
        description:
          'Das gilt auch für e.K., Kleingewerbetreibende & Freiberufler',
        icon: <img src={IconPrivatperson} alt="Icon Privatperson" />,
      },
      {
        id: 'non_profit',
        title: 'Verein oder Genossenschaft',
        description: null,
        icon: <img src={IconVerein} alt="Icon verein" />,
      },
      {
        id: 'company',
        title: 'Kapitalgesellschaft',
        description: 'Dies gilt für GmbH, UG und AG',
        icon: <img src={IconKapital} alt="Icon Kapitalgesellschaft." />,
      },
      {
        id: 'government_entity',
        title: 'Personengesellschaft',
        description:
          'Dies gilt beispielsweise für GbR, OHG, KG, PartG, GmbH & Co, KG',
        icon: <img src={IconPersonen} alt="Icon Personengesellschaft" />,
      },
    ];

    return (
      <Container className="" style={{maxWidth: 480}}>
        <Form onFinish={this.onFinish}>
          <HeaderText>Ich starte ein Projekt als...</HeaderText>
          <FormWrapper>
            {entityTypes.map((type) => (
              <React.Fragment key={type.id}>
                {!(!isGermany && type.id === 'government_entity') && (
                  <ClickableFormItem
                    onClick={() => {
                      this.onFinish(type);
                    }}
                    //$isSelected={type.id === selectedBusinessType}
                  >
                    <IconContainer>{type.icon}</IconContainer>
                    <TextContainer>
                      <Title>{type.title}</Title>
                      {type.description && (
                        <Description>{type.description}</Description>
                      )}
                    </TextContainer>
                  </ClickableFormItem>
                )}
              </React.Fragment>
            ))}

            {/*<p>*/}
            {/*  <b>Ticketprojekt?</b> Kein Problem! Erstelle einfach ein ganz*/}
            {/*  normales Projekt und gib uns danach über das{' '}*/}
            {/*  <a*/}
            {/*    href="#"*/}
            {/*    style={{*/}
            {/*      color: '#3F621A',*/}
            {/*      textDecoration: 'underline',*/}
            {/*    }}*/}
            {/*  >*/}
            {/*    Kontaktformular*/}
            {/*  </a>{' '}*/}
            {/*  Bescheid.*/}
            {/*</p>*/}

            {/*<FormItem>*/}
            {/*  <SecondaryButton*/}
            {/*    type="button"*/}
            {/*    minWidth="120px"*/}
            {/*    onClick={this.onReturn}*/}
            {/*  >*/}
            {/*    <>*/}
            {/*      <ArrowLeftOutlined />*/}
            {/*      <p>Zurück</p>*/}
            {/*    </>*/}
            {/*  </SecondaryButton>*/}
            {/*</FormItem>*/}
          </FormWrapper>
        </Form>

        <ModalV2
          showModal={isUserLoggedIn() ? false : this.state.showLoginModal}
          handelCloseModal={this.handelCloseLoginModal}
        >
          {this.state.showLoginModal && (
            <LoginFormV2
              onLoginSuccess={this.state.onLoginSuccess}
              onRegisterSuccess={this.state.onRegisterSuccess}
            />
          )}
        </ModalV2>
        <ModalV2
          showModal={this.state.showRegisteredModal}
          handelCloseModal={this.handelCloseRegisteredModal}
        >
          {this.state.showRegisteredModal && <RegisteredForm />}
        </ModalV2>
      </Container>
    );
  }
}

const mapStateToProps = (state) => {
  const { project, currentStep } = state.projects;
  return {
    project,
    currentStep,
  };
};

const mapDispatchToProps = (dispatch) => ({
  handleSteps: (step) => dispatch(ProjectsActions.handleSteps(step)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StripeAccount);
